// Dynamic programmatic-SEO page rendering "how to say" / "pronunciation" /
// "difference" entries from /lib/programmaticPages.js.
//
// One React component + three URL patterns (see App.js):
//   /frances/como-dizer/:slug   → type="how"
//   /frances/pronuncia/:slug    → type="pron"
//   /frances/diferenca/:slug    → type="diff"
//
// Each page ships:
//   - Visible TL;DR (Speakable)
//   - Rendered content specific to the template
//   - FAQPage schema (from entry.faq)
//   - BreadcrumbList + BlogPosting/WebPage JSON-LD
//   - Author card → drives PageRank to /prof-lionel
//   - CTA to /aula-gratis

import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/lib/config";
import { formatBlogDate } from "@/lib/formatDate";
import { useCanonical } from "@/lib/useCanonical";
import { AUTHOR, personRef } from "@/lib/authorSchema";
import { getEntry } from "@/lib/programmaticPages";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import { ArrowRight, ArrowLeft, Volume2 } from "lucide-react";

const UI = {
  pt: {
    back: "Voltar ao blog",
    tldrLabel: "Resposta rápida",
    variationsTitle: "Como dizer, palavra por palavra",
    stepsTitle: "Pronúncia passo a passo",
    rulesTitle: "Regras que você precisa saber",
    faqTitle: "Perguntas frequentes",
    aboutAuthor: "Sobre o autor",
    ctaTitle: "Quer treinar isso com um nativo?",
    ctaBody:
      "Aula grátis de 45-60 min com Prof Lionel, nativo de Paris. Sem cartão, sem compromisso.",
    ctaBtn: "Quero minha aula grátis",
    testBtn: "Fazer o teste de nível (5 min)",
    ipaLabel: "IPA",
    updatedLabel: "Atualizado em",
    heroHow: "Como dizer",
    heroPron: "Pronúncia de",
    heroDiff: "Diferença entre",
    heroSuffix: "em francês",
    subtitleHow: (fr) => `A palavra correta em francês é « ${fr} ». Guia curto, com IPA e uso real.`,
    subtitlePron: (ipa) => `Guia rápido pra pronunciar como um parisiense. IPA: ${ipa}`,
    subtitleDiff: (a, b) => `Explicação simples com regra prática, exemplos e teste de bolso.`,
  },
  en: {
    back: "Back to blog",
    tldrLabel: "Quick answer",
    variationsTitle: "How to say it, word by word",
    stepsTitle: "Pronunciation, step by step",
    rulesTitle: "Rules you need to know",
    faqTitle: "Frequently asked questions",
    aboutAuthor: "About the author",
    ctaTitle: "Want to drill this with a native?",
    ctaBody:
      "Free 45-60 min class with Prof Lionel, native Parisian. No card, no commitment.",
    ctaBtn: "Get my free class",
    testBtn: "Take the level test (5 min)",
    ipaLabel: "IPA",
    updatedLabel: "Updated on",
    heroHow: "How to say",
    heroPron: "Pronunciation of",
    heroDiff: "Difference between",
    heroSuffix: "in French",
    subtitleHow: (fr) => `The correct French word is « ${fr} ». Short guide with IPA and real usage.`,
    subtitlePron: (ipa) => `Quick guide to sound like a Parisian. IPA: ${ipa}`,
    subtitleDiff: () => `Simple explanation with a practical rule, examples and a pocket test.`,
  },
  fr: {
    back: "Retour au blog",
    tldrLabel: "Réponse rapide",
    variationsTitle: "Comment le dire, mot à mot",
    stepsTitle: "Prononciation, étape par étape",
    rulesTitle: "Règles à connaître",
    faqTitle: "Questions fréquentes",
    aboutAuthor: "À propos de l'auteur",
    ctaTitle: "Envie de t'entraîner avec un natif ?",
    ctaBody:
      "Cours offert de 45-60 min avec Prof Lionel, natif de Paris. Sans CB, sans engagement.",
    ctaBtn: "Je veux mon cours offert",
    testBtn: "Faire le test de niveau (5 min)",
    ipaLabel: "IPA",
    updatedLabel: "Mis à jour le",
    heroHow: "Comment dire",
    heroPron: "Prononciation de",
    heroDiff: "Différence entre",
    heroSuffix: "en français",
    subtitleHow: (fr) => `Le mot correct est « ${fr} ». Guide court avec IPA et usage réel.`,
    subtitlePron: (ipa) => `Guide rapide pour sonner comme un Parisien. IPA : ${ipa}`,
    subtitleDiff: () => `Explication simple avec règle pratique, exemples et test rapide.`,
  },
};

function tldrText(type, entry, lang) {
  if (type === "how") {
    const variants = entry.variations.slice(0, 3).map((v) => `« ${v.fr} »`).join(", ");
    if (lang === "en")
      return `To say « ${entry[lang]} » in French, the base form is « ${entry.fr} » (${entry.ipa}). Common variants: ${variants}. Choose the register (formal, neutral, casual) based on your interlocutor.`;
    if (lang === "fr")
      return `Pour dire « ${entry.pt} » (portugais) en français, la forme de base est « ${entry.fr} » (${entry.ipa}). Variantes : ${variants}. Choisis le registre selon l'interlocuteur.`;
    return `Pra dizer « ${entry.pt} » em francês, a forma base é « ${entry.fr} » (${entry.ipa}). Variantes comuns: ${variants}. Escolha o registro (formal, neutro, casual) conforme o interlocutor.`;
  }
  if (type === "pron") {
    if (lang === "en")
      return `« ${entry.word} » is pronounced ${entry.ipa}. Three key moves: French J/uvular R (as needed), silent final consonants, no diphthong on the last vowel. Full method with drills below.`;
    if (lang === "fr")
      return `« ${entry.word} » se prononce ${entry.ipa}. Trois clés : J français / R uvulaire, consonnes finales muettes, pas de diphtongue sur la voyelle finale. Méthode complète ci-dessous.`;
    return `« ${entry.word} » se pronuncia ${entry.ipa}. Três chaves: J francês / R uvular quando aparece, consoantes finais mudas, sem ditongo na vogal final. Método completo abaixo.`;
  }
  // diff
  return entry.intro[lang] || entry.intro.pt;
}

function faqItems(entry, type, lang) {
  if (type === "how") {
    return Object.values(entry.faq).map((qset) => qset[lang] || qset.pt);
  }
  if (type === "pron") {
    return (entry.faq[lang] || entry.faq.pt) || [];
  }
  // diff
  return (entry.faq[lang] || entry.faq.pt) || [];
}

export default function ProgrammaticPage({ type }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const ui = UI[lang] || UI.pt;
  const entry = getEntry(type, slug);
  useCanonical(entry ? `/frances/${type === "how" ? "como-dizer" : type === "pron" ? "pronuncia" : "diferenca"}/${slug}` : "/blog");

  // Programmatic audio: only shown when the MP3 was actually generated
  // (see /app/scripts/generate_all_audios.py `prog` batch). Diff-type pages
  // don't get audio — those are text-explanation only.
  const audioSegment =
    type === "how" ? "como-dizer" : type === "pron" ? "pronuncia" : null;
  const audioUrl = audioSegment
    ? `${process.env.PUBLIC_URL || ""}/audio/prog-${audioSegment}-${slug}.mp3`
    : null;
  const [audioExists, setAudioExists] = React.useState(false);
  React.useEffect(() => {
    if (!audioUrl) return;
    let cancelled = false;
    // Dev servers (React CRA) return 200 with text/html on missing files via
    // SPA fallback, so a naive HEAD check is unreliable. We inspect the
    // Content-Type: only render the player when it's an audio MIME.
    fetch(audioUrl, { method: "HEAD" })
      .then((r) => {
        if (!r.ok) return false;
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        return ct.startsWith("audio/");
      })
      .then((ok) => !cancelled && setAudioExists(ok))
      .catch(() => !cancelled && setAudioExists(false));
    return () => {
      cancelled = true;
    };
  }, [audioUrl]);

  useEffect(() => {
    if (!entry) navigate("/blog");
  }, [entry, navigate]);

  useEffect(() => {
    if (!entry) return;
    const canonicalSegment =
      type === "how" ? "como-dizer" : type === "pron" ? "pronuncia" : "diferenca";
    const url = `https://npdlbr.com.br/frances/${canonicalSegment}/${slug}`;
    const heroTitle =
      type === "how"
        ? `${ui.heroHow} « ${entry.pt} » ${ui.heroSuffix}`
        : type === "pron"
        ? `${ui.heroPron} « ${entry.word} » ${ui.heroSuffix}`
        : `${ui.heroDiff} « ${entry.a} » ${lang === "en" ? "and" : lang === "fr" ? "et" : "e"} « ${entry.b} » ${ui.heroSuffix}`;
    const description = tldrText(type, entry, lang).slice(0, 300);

    // Meta tags
    const setMeta = (selector, attr, value, content) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      } else if (!el.dataset.original) {
        el.dataset.original = el.getAttribute("content") || "";
      }
      el.setAttribute("content", content);
      return { el, created };
    };
    const handles = [
      setMeta('meta[name="description"]', "name", "description", description),
      setMeta('meta[property="og:title"]', "property", "og:title", heroTitle),
      setMeta('meta[property="og:description"]', "property", "og:description", description),
      setMeta('meta[property="og:url"]', "property", "og:url", url),
      setMeta('meta[property="og:type"]', "property", "og:type", "article"),
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", heroTitle),
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description),
    ];

    // JSON-LD
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://npdlbr.com.br/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://npdlbr.com.br/blog" },
        { "@type": "ListItem", position: 3, name: heroTitle, item: url },
      ],
    };
    const article = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: heroTitle,
      description,
      author: personRef(),
      publisher: {
        "@type": "Organization",
        name: "Na Ponta Da Língua Brasil",
        logo: {
          "@type": "ImageObject",
          url: "https://customer-assets.emergentagent.com/job_french-for-all-1/artifacts/bv8f2u2u_Capture%20d%E2%80%99e%CC%81cran%202026-06-19%20a%CC%80%2016.45.23.png",
        },
      },
      datePublished: entry.updated,
      dateModified: entry.updated,
      inLanguage: lang,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".prog-tldr", ".prog-heading"],
      },
    };
    const items = faqItems(entry, type, lang);
    const faqPage = items.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: items.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

    const emit = (obj, tag) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.dynamic = tag;
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
      return el;
    };
    const els = [emit(breadcrumb, "prog-breadcrumb"), emit(article, "prog-article")];
    if (faqPage) els.push(emit(faqPage, "prog-faq"));

    const prevTitle = document.title;
    document.title = `${heroTitle} · npdlbr.com.br`;

    return () => {
      els.forEach((el) => el.remove());
      handles.forEach(({ el, created }) => {
        if (created) el.remove();
        else if (el.dataset.original !== undefined) {
          el.setAttribute("content", el.dataset.original);
          delete el.dataset.original;
        }
      });
      document.title = prevTitle;
    };
  }, [entry, slug, type, lang, ui]);

  if (!entry) return null;

  const items = faqItems(entry, type, lang);
  const heroTitle =
    type === "how"
      ? `${ui.heroHow} « ${entry.pt} » ${ui.heroSuffix}`
      : type === "pron"
      ? `${ui.heroPron} « ${entry.word} » ${ui.heroSuffix}`
      : `${ui.heroDiff} « ${entry.a} » ${lang === "en" ? "and" : lang === "fr" ? "et" : "e"} « ${entry.b} » ${ui.heroSuffix}`;

  const subtitle =
    type === "how"
      ? ui.subtitleHow(entry.fr)
      : type === "pron"
      ? ui.subtitlePron(entry.ipa)
      : ui.subtitleDiff(entry.a, entry.b);

  return (
    <div data-testid="programmatic-page" className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]">
      <header className="border-b border-[#e5e3db]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#1e2a5a] font-display italic">
              L
            </span>
            <span className="font-display text-sm sm:text-base">{BRAND.name}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/blog" className="inline-flex items-center gap-1 text-xs sm:text-sm link-underline">
              <ArrowLeft className="h-3.5 w-3.5" /> {ui.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10 sm:py-16">
        <p className="text-xs uppercase tracking-[0.22em] text-[#d9381e]">
          {type === "how"
            ? lang === "en" ? "How to say · programmatic guide" : lang === "fr" ? "Comment dire · guide" : "Como dizer · guia rápido"
            : type === "pron"
            ? lang === "en" ? "Pronunciation · programmatic guide" : lang === "fr" ? "Prononciation · guide" : "Pronúncia · guia rápido"
            : lang === "en" ? "Difference · programmatic guide" : lang === "fr" ? "Différence · guide" : "Diferença · guia rápido"}
        </p>

        <h1 className="prog-heading font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          {heroTitle}
        </h1>

        <p className="mt-5 text-base sm:text-lg text-[#5c5c5c] leading-relaxed">
          {subtitle}
        </p>

        {/* TLDR — Speakable target */}
        <aside
          className="prog-tldr mt-8 rounded-xl border-l-4 border-[#d9381e] bg-[#f0efea] px-5 py-4 sm:px-6 sm:py-5"
          data-testid="prog-tldr"
        >
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#d9381e] font-medium">
            {ui.tldrLabel}
          </p>
          <p className="mt-2 text-base sm:text-lg leading-relaxed text-[#1e2a5a]">
            {tldrText(type, entry, lang)}
          </p>
        </aside>

        {/* Programmatic audio (Prof Lionel's cloned voice, ElevenLabs).
            Rendered ONLY when the MP3 exists — the HEAD probe in the
            effect above sets audioExists=true. If the file isn't there,
            the entire block silently disappears, keeping the page clean. */}
        {audioExists && audioUrl && (
          <div
            className="mt-6 rounded-xl border border-[#e5e3db] bg-white p-4 sm:p-5"
            data-testid="prog-audio-player"
          >
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d9381e] font-medium flex items-center gap-2">
              <Volume2 className="h-3.5 w-3.5" />
              {lang === "fr"
                ? "Écoute Prof Lionel"
                : lang === "en"
                ? "Listen to Prof Lionel"
                : "Escute o Prof Lionel"}
            </p>
            <audio
              controls
              preload="none"
              className="mt-3 w-full"
              src={audioUrl}
              data-testid="prog-audio-src"
            />
          </div>
        )}

        {/* Type-specific body */}
        {type === "how" && (
          <section className="mt-12">
            <h2 className="prog-heading font-display text-2xl sm:text-3xl tracking-tight">
              {ui.variationsTitle}
            </h2>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="border-b-2 border-[#1e2a5a] text-left">
                    <th className="py-3 pr-4 font-display text-[#1e2a5a]">Français</th>
                    <th className="py-3 pr-4 font-display text-[#1e2a5a]">IPA</th>
                    <th className="py-3 font-display text-[#1e2a5a]">
                      {lang === "en" ? "Usage" : lang === "fr" ? "Usage" : "Uso"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entry.variations.map((v) => (
                    <tr key={v.fr} className="border-b border-[#e5e3db]">
                      <td className="py-3 pr-4 font-medium text-[#d9381e]">{v.fr}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-[#5c5c5c]">{v.ipa}</td>
                      <td className="py-3 text-[#3a3a3a]">{v[lang] || v.pt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {type === "pron" && (
          <section className="mt-12">
            <h2 className="prog-heading font-display text-2xl sm:text-3xl tracking-tight flex items-center gap-3">
              <Volume2 className="h-6 w-6 text-[#d9381e]" />
              {ui.stepsTitle}
            </h2>
            <ol className="mt-6 space-y-4 list-decimal list-inside">
              {(entry.steps[lang] || entry.steps.pt).map((s, i) => (
                <li key={i} className="text-base sm:text-lg leading-[1.7] text-[#3a3a3a]">
                  {s}
                </li>
              ))}
            </ol>
            <p className="mt-6 text-sm text-[#5c5c5c] italic">
              {ui.ipaLabel}: <span className="font-mono text-[#1e2a5a]">{entry.ipa}</span>
            </p>
          </section>
        )}

        {type === "diff" && (
          <section className="mt-12">
            <h2 className="prog-heading font-display text-2xl sm:text-3xl tracking-tight">
              {ui.rulesTitle}
            </h2>
            <ul className="mt-6 space-y-4">
              {(entry.rules[lang] || entry.rules.pt).map((r, i) => (
                <li key={i} className="flex gap-3 text-base sm:text-lg text-[#3a3a3a]">
                  <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-[#d9381e]" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* FAQ */}
        {items.length > 0 && (
          <section className="mt-16" data-testid="prog-faq">
            <h2 className="prog-heading font-display text-2xl sm:text-3xl tracking-tight">
              {ui.faqTitle}
            </h2>
            <div className="mt-6 divide-y divide-[#e5e3db] border-t border-b border-[#e5e3db]">
              {items.map((f) => (
                <details key={f.q} className="group py-4">
                  <summary className="flex cursor-pointer items-start justify-between gap-4 font-display text-lg text-[#1e2a5a] hover:text-[#d9381e] transition-colors">
                    <span>{f.q}</span>
                    <span className="mt-1 text-2xl leading-none text-[#d9381e] group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 pr-8 text-base leading-relaxed text-[#3a3a3a]">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Author card */}
        <section
          className="mt-16 rounded-2xl border border-[#e5e3db] bg-white p-6 sm:p-8 flex gap-5 items-start"
          data-testid="prog-author-card"
        >
          <img
            src={AUTHOR.image}
            alt={`${AUTHOR.name} — ${AUTHOR.jobTitle}`}
            width="80"
            height="80"
            loading="lazy"
            className="w-20 h-20 rounded-full object-cover shrink-0 border border-[#e5e3db]"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d9381e]">
              {ui.aboutAuthor}
            </p>
            <Link
              to="/prof-lionel"
              className="mt-1 inline-block font-display text-xl sm:text-2xl text-[#1e2a5a] hover:text-[#d9381e] transition-colors"
              data-testid="prog-author-link"
            >
              {AUTHOR.name}
            </Link>
            <p className="mt-1 text-sm text-[#5c5c5c]">{AUTHOR.jobTitle}</p>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#3a3a3a]">
              {AUTHOR.description}
            </p>
          </div>
        </section>

        {/* CTA */}
        <aside
          className="mt-20 rounded-2xl bg-[#1e2a5a] text-[#f4f1ea] p-8 sm:p-12"
          data-testid="prog-cta"
        >
          <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">
            {ui.ctaTitle}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#f4f1ea]/85">{ui.ctaBody}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/aula-gratis"
              className="group inline-flex items-center gap-2 rounded-full bg-[#f4f1ea] px-6 py-3 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#d9381e] hover:text-[#f4f1ea]"
              data-testid="prog-cta-book"
            >
              {ui.ctaBtn}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/teste-de-nivel"
              className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/40 px-6 py-3 text-sm font-medium text-[#f4f1ea] transition-colors hover:border-[#f4f1ea] hover:bg-[#f4f1ea]/10"
              data-testid="prog-cta-test"
            >
              {ui.testBtn}
            </Link>
          </div>
        </aside>

        <p className="mt-16 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
          {ui.updatedLabel}{" "}
          <time dateTime={entry.updated}>{formatBlogDate(entry.updated, lang)}</time>
        </p>
      </main>
    </div>
  );
}
